import {
  JSONAPI_HEADERS, flattenPayload, writeBody, jsonApiGet, jsonApiGetAll, jsonApiWrite, publicV3Url,
} from '../utils/jsonApi';

// %d is the block, %s the nested record (a request) where the endpoint has one.
const blockPath = (endpoint, blockId, id) => (
  publicV3Url(endpoint.replace('%d', blockId).replace('%s', id))
);

// Every write lands in the same two store handlers, tagged with its block so the page can drop a
// late response for a block the employee has since left. The page refetches the block on success
// and renders a refusal beside the control that caused it.
const write = (blockId, type, url, body) => {
  jsonApiWrite(url, type, body).then((result) => {
    if (result.ok) {
      SelfScheduleStore.onWriteSuccess(blockId);
    } else {
      SelfScheduleStore.onWriteError(blockId, result.errors);
    }
  });
};

export default class SelfScheduleCollection extends Backbone.Collection {
  // The collection itself holds the employee's blocks. One block's page is resolved by
  // SelfScheduleStore.onBlockDataHandler from the seven reads below and handed to the page whole.
  parse(response) {
    return flattenPayload(response);
  }

  dispatchCallback(payload) {
    const { actionType } = payload;
    const { ActionTypes, EndPoints } = appConstants;
    const data = payload.postData || {};

    switch (actionType) {
      case ActionTypes.GET_SELF_SCHEDULE_BLOCKS:
        Utils.setMakeshiftTokenHeader();

        SelfScheduleStore.fetch({
          url: blockPath(EndPoints.SELF_SCHEDULING_BLOCKS_PATH),
          type: 'GET',
          headers: JSONAPI_HEADERS,
          // Nested filters rely on jQuery's default $.param bracket encoding, so this fetch must
          // not pass traditional: true.
          data: payload.postData,
          success: SelfScheduleStore.onBlocksHandler,
        });
        break;

      case ActionTypes.GET_SELF_SCHEDULE_BLOCK_DATA: {
        Utils.setMakeshiftTokenHeader();

        const { blockId } = data;

        // Fired together, not sequenced off the block's status: eligible_shifts answers 200 with
        // an empty list when the employee's window is shut, so branching first would cost a round
        // trip and buy nothing.
        $.when(
          // The pool rides along as `included`, which is what the read-only states render from
          // once eligible_shifts has gone empty.
          jsonApiGet(blockPath(EndPoints.SELF_SCHEDULING_BLOCK_PATH, blockId), {
            include: 'shift_requirements',
          }),
          jsonApiGet(blockPath(EndPoints.SELF_SCHEDULING_BLOCK_GROUPS_PATH, blockId)),
          jsonApiGetAll(blockPath(EndPoints.SELF_SCHEDULING_ELIGIBLE_SHIFTS_PATH, blockId)),
          jsonApiGetAll(blockPath(EndPoints.SELF_SCHEDULING_REQUESTS_PATH, blockId)),
          jsonApiGet(blockPath(EndPoints.SELF_SCHEDULING_PROGRESS_PATH, blockId)),
          jsonApiGetAll(blockPath(EndPoints.SELF_SCHEDULING_SUBMISSIONS_PATH, blockId)),
          jsonApiGetAll(blockPath(EndPoints.SELF_SCHEDULING_NA_ENTRIES_PATH, blockId)),
        ).then((block, groups, eligible, requests, progress, submissions, naEntries) => {
          SelfScheduleStore.onBlockDataHandler(blockId, {
            block, groups, eligible, requests, progress, submissions, naEntries,
          });
        });
        break;
      }

      case ActionTypes.SELF_SCHEDULE_REQUEST_SHIFT:
        write(
          data.blockId,
          'POST',
          blockPath(EndPoints.SELF_SCHEDULING_REQUESTS_PATH, data.blockId),
          // The one cast the API needs: ids arrive as JSON:API strings and this attribute is an
          // integer.
          writeBody('self_scheduling_request', {
            shift_requirement_id: Number(data.shiftRequirementId),
          }),
        );
        break;

      // DELETE rather than PUT …/withdraw: same effect and permissions, and the page refetches
      // anyway so the 200 body the PUT returns would go unread.
      case ActionTypes.SELF_SCHEDULE_CANCEL_REQUEST:
        write(
          data.blockId,
          'DELETE',
          blockPath(EndPoints.SELF_SCHEDULING_REQUEST_PATH, data.blockId, data.requestId),
        );
        break;

      case ActionTypes.SELF_SCHEDULE_SUBMIT:
        write(data.blockId, 'PUT', blockPath(EndPoints.SELF_SCHEDULING_SUBMIT_PATH, data.blockId));
        break;

      case ActionTypes.SELF_SCHEDULE_REOPEN:
        write(data.blockId, 'PUT', blockPath(EndPoints.SELF_SCHEDULING_REOPEN_PATH, data.blockId));
        break;

      default:
        break;
    }
  }
}
